import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash2, Printer, FileText } from "lucide-react";

interface LabelContent {
  type: 'heading' | 'paragraph';
  content: string;
}

const LabelGeneratorPage = () => {
  const [companyName, setCompanyName] = useState('GLOBAL MFGS & EXPORTS');
  const [itemPrefix, setItemPrefix] = useState('GMKB');
  const [rangeStart, setRangeStart] = useState('101');
  const [rangeEnd, setRangeEnd] = useState('200');
  const [labelWidth, setLabelWidth] = useState('3.4');
  const [labelHeight, setLabelHeight] = useState('2.47');
  const [dimensionUnit, setDimensionUnit] = useState('cm');
  const [columnsPerRow, setColumnsPerRow] = useState('6');
  const [pageMargin, setPageMargin] = useState('0.5');
  const [bodyFont, setBodyFont] = useState("'Segoe UI', Tahoma, Geneva, Verdana, sans-serif");
  const [companyFontSize, setCompanyFontSize] = useState('8');
  const [companyFontFamily, setCompanyFontFamily] = useState("'Georgia', serif");
  const [companyLetterSpacing, setCompanyLetterSpacing] = useState('0.5');
  const [paragraphFontSize, setParagraphFontSize] = useState('9');
  const [paragraphFontFamily, setParagraphFontFamily] = useState("'Courier New', monospace");
  const [paragraphLetterSpacing, setParagraphLetterSpacing] = useState('1');
  const [labelMargin, setLabelMargin] = useState('0');
  const [gridGap, setGridGap] = useState('0.1');
  const [paragraphMargin, setParagraphMargin] = useState('9');
  const [labels, setLabels] = useState<JSX.Element[]>([]);
  const [labelContent, setLabelContent] = useState<LabelContent[]>([
    { type: 'heading', content: '{COMPANY}' },
    { type: 'paragraph', content: 'ITEM # {PREFIX}{NUMBER}' },
    { type: 'paragraph', content: 'SIZE:' },
    { type: 'paragraph', content: 'FINISH<br>CODE:' }
  ]);

  const convertToMM = (value: number, unit: string): number => {
    switch (unit) {
      case 'mm':
        return value;
      case 'cm':
        return value * 10;
      case 'in':
        return value * 25.4;
      default:
        return value;
    }
  };

  const addHeading = () => {
    const headingText = prompt('Enter heading text:');
    if (headingText && headingText.trim()) {
      setLabelContent(prev => [...prev, { type: 'heading', content: headingText.trim() }]);
    }
  };

  const addParagraph = () => {
    const paragraphText = prompt('Enter paragraph text (use {PREFIX} for item prefix, {NUMBER} for item number):');
    if (paragraphText && paragraphText.trim()) {
      setLabelContent(prev => [...prev, { type: 'paragraph', content: paragraphText.trim() }]);
    }
  };

  const removeLastElement = () => {
    if (labelContent.length > 1) {
      setLabelContent(prev => prev.slice(0, -1));
    } else {
      alert('Cannot remove the last element. At least one element must remain.');
    }
  };

  const generateLabels = () => {
    const start = parseInt(rangeStart);
    const end = parseInt(rangeEnd);
    const width = parseFloat(labelWidth);
    const height = parseFloat(labelHeight);
    const columns = parseInt(columnsPerRow);
    const margin = parseFloat(pageMargin);
    const compFontSize = parseFloat(companyFontSize);
    const compLetterSpacing = parseFloat(companyLetterSpacing);
    const paraFontSize = parseFloat(paragraphFontSize);
    const paraLetterSpacing = parseFloat(paragraphLetterSpacing);
    const lblMargin = parseFloat(labelMargin);
    const gap = parseFloat(gridGap);
    const paraMargin = parseFloat(paragraphMargin);

    if (!companyName || !itemPrefix || isNaN(start) || isNaN(end) || 
        isNaN(width) || isNaN(height) || isNaN(columns) || isNaN(margin) ||
        isNaN(compFontSize) || isNaN(compLetterSpacing) || isNaN(paraFontSize) || isNaN(paraLetterSpacing) ||
        isNaN(lblMargin) || isNaN(gap) || isNaN(paraMargin) ||
        start > end || width <= 0 || height <= 0 || columns <= 0 || margin < 0 || lblMargin < 0 || gap < 0 || paraMargin < 0) {
      alert('Please fill all fields with valid values. Range end must be greater than or equal to start, and dimensions must be positive numbers.');
      return;
    }

    const widthMM = convertToMM(width, dimensionUnit);
    const heightMM = convertToMM(height, dimensionUnit);

    const generatedLabels: JSX.Element[] = [];
    
    for (let i = start; i <= end; i++) {
      const labelElements: JSX.Element[] = [];
      
      labelContent.forEach((element, index) => {
        let processedContent = element.content
          .replace(/{COMPANY}/g, companyName)
          .replace(/{PREFIX}/g, itemPrefix)
          .replace(/{NUMBER}/g, i.toString());
        
        if (element.type === 'heading') {
          labelElements.push(
            <h3 
              key={index}
              style={{
                fontSize: `${compFontSize}px`,
                fontFamily: companyFontFamily,
                letterSpacing: `${compLetterSpacing}px`,
                margin: '2px 0',
                fontWeight: 600,
                textAlign: 'center' as const,
                textDecoration: 'underline'
              }}
            >
              {processedContent}
            </h3>
          );
        } else {
          const parts = processedContent.split('<br>');
          parts.forEach((part, partIndex) => (
            <p 
              key={`${index}-${partIndex}`}
              style={{
                fontSize: `${paraFontSize}px`,
                fontFamily: paragraphFontFamily,
                letterSpacing: `${paraLetterSpacing}px`,
                margin: `${paraMargin}px 0`,
                fontWeight: 'bold'
              }}
            >
              {part}
            </p>
          ));
        }
      });

      generatedLabels.push(
        <div
          key={i}
          className="border border-black"
          style={{
            width: `${widthMM}mm`,
            height: `${heightMM}mm`,
            padding: `${lblMargin}px`,
            boxSizing: 'border-box',
            textAlign: 'left' as const
          }}
        >
          {labelElements}
        </div>
      );
    }

    setLabels(generatedLabels);
  };

  const printLabels = () => {
    if (labels.length === 0) {
      alert('Please generate labels first before printing.');
      return;
    }
    window.print();
  };

  useEffect(() => {
    generateLabels();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        @page {
          size: A4;
          margin: ${pageMargin}mm;
        }
        
        @media print {
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
        }
        
        @media screen {
          .print-only {
            display: none !important;
          }
        }
      `}</style>
      
      <div className="no-print">
        <Card className="m-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Label Generator Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="companyName">Company Name:</Label>
                <Input
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="itemPrefix">Item Prefix:</Label>
                <Input
                  id="itemPrefix"
                  value={itemPrefix}
                  onChange={(e) => setItemPrefix(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="rangeStart">Range Start:</Label>
                <Input
                  id="rangeStart"
                  type="number"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  min="1"
                />
              </div>
              <div>
                <Label htmlFor="rangeEnd">Range End:</Label>
                <Input
                  id="rangeEnd"
                  type="number"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  min="1"
                />
              </div>
            </div>

            <Separator />

            {/* Label Dimensions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="labelWidth">Label Width:</Label>
                <Input
                  id="labelWidth"
                  type="number"
                  value={labelWidth}
                  onChange={(e) => setLabelWidth(e.target.value)}
                  min="1"
                  step="0.1"
                />
              </div>
              <div>
                <Label htmlFor="labelHeight">Label Height:</Label>
                <Input
                  id="labelHeight"
                  type="number"
                  value={labelHeight}
                  onChange={(e) => setLabelHeight(e.target.value)}
                  min="1"
                  step="0.1"
                />
              </div>
              <div>
                <Label htmlFor="dimensionUnit">Unit:</Label>
                <Select value={dimensionUnit} onValueChange={setDimensionUnit}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cm">Centimeters (cm)</SelectItem>
                    <SelectItem value="mm">Millimeters (mm)</SelectItem>
                    <SelectItem value="in">Inches (in)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="columnsPerRow">Columns per Row:</Label>
                <Input
                  id="columnsPerRow"
                  type="number"
                  value={columnsPerRow}
                  onChange={(e) => setColumnsPerRow(e.target.value)}
                  min="1"
                  max="6"
                />
              </div>
            </div>

            <Separator />

            {/* Page and Layout Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="pageMargin">Page Margin (mm):</Label>
                <Input
                  id="pageMargin"
                  type="number"
                  value={pageMargin}
                  onChange={(e) => setPageMargin(e.target.value)}
                  min="0"
                  step="1"
                />
              </div>
              <div>
                <Label htmlFor="bodyFont">Body Font:</Label>
                <Select value={bodyFont} onValueChange={setBodyFont}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="'Segoe UI', Tahoma, Geneva, Verdana, sans-serif">Segoe UI</SelectItem>
                    <SelectItem value="Arial, sans-serif">Arial</SelectItem>
                    <SelectItem value="'Times New Roman', serif">Times New Roman</SelectItem>
                    <SelectItem value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</SelectItem>
                    <SelectItem value="Georgia, serif">Georgia</SelectItem>
                    <SelectItem value="'Courier New', monospace">Courier New</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="labelMargin">Label Margin (px):</Label>
                <Input
                  id="labelMargin"
                  type="number"
                  value={labelMargin}
                  onChange={(e) => setLabelMargin(e.target.value)}
                  min="0"
                  max="20"
                  step="1"
                />
              </div>
              <div>
                <Label htmlFor="gridGap">Grid Gap (mm):</Label>
                <Input
                  id="gridGap"
                  type="number"
                  value={gridGap}
                  onChange={(e) => setGridGap(e.target.value)}
                  min="0"
                  max="20"
                  step="0.5"
                />
              </div>
            </div>

            <Separator />

            {/* Font Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="companyFontSize">Company Font Size (px):</Label>
                <Input
                  id="companyFontSize"
                  type="number"
                  value={companyFontSize}
                  onChange={(e) => setCompanyFontSize(e.target.value)}
                  min="8"
                  max="24"
                  step="1"
                />
              </div>
              <div>
                <Label htmlFor="companyFontFamily">Company Font:</Label>
                <Select value={companyFontFamily} onValueChange={setCompanyFontFamily}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="'Georgia', serif">Georgia</SelectItem>
                    <SelectItem value="'Times New Roman', serif">Times New Roman</SelectItem>
                    <SelectItem value="Arial, sans-serif">Arial</SelectItem>
                    <SelectItem value="'Segoe UI', Tahoma, Geneva, Verdana, sans-serif">Segoe UI</SelectItem>
                    <SelectItem value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</SelectItem>
                    <SelectItem value="'Courier New', monospace">Courier New</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="companyLetterSpacing">Company Letter Spacing (px):</Label>
                <Input
                  id="companyLetterSpacing"
                  type="number"
                  value={companyLetterSpacing}
                  onChange={(e) => setCompanyLetterSpacing(e.target.value)}
                  min="0"
                  max="5"
                  step="0.1"
                />
              </div>
              <div>
                <Label htmlFor="paragraphFontSize">Paragraph Font Size (px):</Label>
                <Input
                  id="paragraphFontSize"
                  type="number"
                  value={paragraphFontSize}
                  onChange={(e) => setParagraphFontSize(e.target.value)}
                  min="6"
                  max="20"
                  step="1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="paragraphFontFamily">Paragraph Font:</Label>
                <Select value={paragraphFontFamily} onValueChange={setParagraphFontFamily}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="'Courier New', monospace">Courier New</SelectItem>
                    <SelectItem value="Arial, sans-serif">Arial</SelectItem>
                    <SelectItem value="'Times New Roman', serif">Times New Roman</SelectItem>
                    <SelectItem value="'Segoe UI', Tahoma, Geneva, Verdana, sans-serif">Segoe UI</SelectItem>
                    <SelectItem value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</SelectItem>
                    <SelectItem value="Georgia, serif">Georgia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="paragraphLetterSpacing">Paragraph Letter Spacing (px):</Label>
                <Input
                  id="paragraphLetterSpacing"
                  type="number"
                  value={paragraphLetterSpacing}
                  onChange={(e) => setParagraphLetterSpacing(e.target.value)}
                  min="0"
                  max="5"
                  step="0.1"
                />
              </div>
              <div>
                <Label htmlFor="paragraphMargin">Paragraph Margin (px):</Label>
                <Input
                  id="paragraphMargin"
                  type="number"
                  value={paragraphMargin}
                  onChange={(e) => setParagraphMargin(e.target.value)}
                  min="0"
                  max="20"
                  step="0.5"
                />
              </div>
            </div>

            <Separator />

            {/* Content Management */}
            <div>
              <Label>Label Content Management:</Label>
              <div className="flex gap-2 mt-2">
                <Button onClick={addHeading} variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Heading
                </Button>
                <Button onClick={addParagraph} variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Paragraph
                </Button>
                <Button onClick={removeLastElement} variant="outline" size="sm">
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove Last
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="contentPreview">Content Preview:</Label>
              <div 
                id="contentPreview" 
                className="border border-gray-300 p-3 min-h-[100px] bg-gray-100 font-mono text-sm mt-2"
              >
                {labelContent.map((element, index) => (
                  element.type === 'heading' ? (
                    <h3 key={index} className="font-bold">{element.content}</h3>
                  ) : (
                    <p key={index}>{element.content}</p>
                  )
                ))}
              </div>
            </div>

            <Separator />

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button onClick={generateLabels} className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Generate Labels
              </Button>
              <Button onClick={printLabels} variant="outline" className="flex items-center gap-2">
                <Printer className="h-4 w-4" />
                Print Labels
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Label Preview */}
      <div className="bg-white m-4 p-4 rounded-lg shadow-sm">
        <div 
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${columnsPerRow}, 1fr)`,
            gap: `${gridGap}mm`,
            justifyContent: 'space-between',
            alignContent: 'space-between'
          }}
        >
          {labels}
        </div>
      </div>
    </div>
  );
};

export default LabelGeneratorPage;
